import React, {useState, useEffect, useMemo} from "react";
import ExpressOnBoardingModal from "./ExpressOnBoardingModal";
import useOnBoarding from "src/core/checkout/hooks/useOnBoarding";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import AccountVerificationCheckModal from "../../components/AccountVerificationCheckModal";
import get from "lodash/get";
import AuthenticationHandler from "src/core/authentication/authenticationHandler";
import {FormHeader} from "../../../common/components/collections/form/forms";
import {WizardProgressIndicator} from "../../../common/components/modules/WizardProgressIndicator";
import styled from "styled-components";
import RegistrationBuilder from "../../components/registrationBuilder";
import PhoneConfirmationWarning from "../../components/PhoneConfirmationWarning";
import EmailConfirmationWarning from "../../components/EmailConfirmationWarning";
import useFeatureToggles from "../../../common/hooks/useFeatureToggles";
import useDeliveryType from "../../../deliveries/hooks/useDeliveryType";
import useInventoryType from "src/core/inventories/useInventoryType";
import {makeIdentityVerification} from "src/integrations/identityVerification/factory";
import IdentityVerificationForm from "src/integrations/identityVerification/components/IdentityVerificationForm";
import useAccountVerification from "src/core/authentication/hooks/useAccountVerification";
import useIdentityVerificationProps from "src/integrations/identityVerification/hooks/useIdentityVerificationProps";

function ExpressOnBoarding({onSuccess, inProgress, closeOnBoarding, options}) {
  const [, authApi] = useAuthentication();
  const [builder] = useState(new RegistrationBuilder());
  const [onBoardingState, OnBoarding] = useOnBoarding();
  const [step, setStep] = useState(Steps.PERSONAL_INFO);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [deliveryType] = useDeliveryType();
  const [inventoryType] = useInventoryType();
  const [identityVerificationId, setIdentityVerificationId] = useState(null);
  const toggles = useFeatureToggles();
  const idVerificationService = makeIdentityVerification(
    toggles.getIdentityVerificationServiceName()
  );
  const [, api] = useAccountVerification();
  const {signupModalTitle, signupModalSubtitle, signupButtonLabel} =
    useIdentityVerificationProps();

  useEffect(() => {
    return authApi.addListener(
      new RegistrationHandler(() => {
        if (idVerificationService.requiresVerificationOnSignUp()) {
          setStep(Steps.IDENTITY_VERIFICATION);
        } else {
          setStep(Steps.CONFIRMATION);
        }
      })
    );
  }, [authApi, toggles]);

  function onSubmitPersonalInfo(values) {
    if (values.saveCheckoutInfo) {
      register();
    } else {
      cacheCheckoutInfo(builder.build());
    }
  }

  function cacheCheckoutInfo(info) {
    OnBoarding.setOnBoardingData(info);
    onSuccess();
  }

  function register() {
    const payload = builder.build();
    setPersonalInfo(payload);
    authApi.register(payload);
  }

  function requestLoginVerification({verificationId}) {
    const {phoneNumber, email} = builder.build();
    api.accountVerification({phoneNumber, email});
    setStep(Steps.CONFIRMATION);

    setIdentityVerificationId(verificationId);
  }

  const steps = useMemo(
    () => ["Personal", "Create Account", "Checkout"].map(name => ({name})),
    []
  );

  const _options = options || {};
  const verificationCheckProps = {
    titleComponent: _options.toCheckout ? (
      <CreateAccountTitle
        withProgress={true}
        steps={steps}
        currentStep={1}
        loginMode={toggles.loginWithEmail() ? "email" : "phone"}
        email={get(personalInfo, "email")}
      />
    ) : (
      <CreateAccountTitle
        loginMode={toggles.loginWithEmail() ? "email" : "phone"}
        email={get(personalInfo, "email")}
      />
    ),
    replaceOnClose: _options.replaceOnClose,
    isOpen: inProgress,
    defaultPhone: get(personalInfo, "phoneNumber"),
    defaultEmail: get(personalInfo, "email"),
    extraInfo: personalInfo,
    closeModal: closeOnBoarding,
    onSuccess: onSuccess,
    identityVerificationId,
  };

  const expressOnBoardingProps = {
    closeModal: closeOnBoarding,
    replaceOnClose: _options.replaceOnClose,
    isOpen: inProgress,
    initialValues: onBoardingState.data,
    onSuccess: onSubmitPersonalInfo,
    title: signupModalTitle,
    subtitle: signupModalSubtitle,
    submitLabel: _options.toCheckout ? "Checkout" : signupButtonLabel,
    showSaveCheckoutInfo:
      toggles.registrationOptional({inventoryType, deliveryType}) && _options.toCheckout,
  };

  return (
    <>
      {step === Steps.PERSONAL_INFO && (
        <ExpressOnBoardingModal {...expressOnBoardingProps} builder={builder} />
      )}

      {step === Steps.IDENTITY_VERIFICATION && (
        <IdentityVerificationForm
          identityVerification={idVerificationService}
          onSuccess={requestLoginVerification}
          onCancel={() => requestLoginVerification({})}
        />
      )}

      {step === Steps.CONFIRMATION && (
        <AccountVerificationCheckModal
          {...verificationCheckProps}
          backAction={() => setStep(Steps.PERSONAL_INFO)}
        />
      )}
    </>
  );
}
ExpressOnBoarding.isEnabled = toggles => ["phone", "email"].includes(toggles.loginMode());
ExpressOnBoarding.isDone = ({loggedIn, onBoardingStateData}) =>
  onBoardingStateData || loggedIn;

class RegistrationHandler extends AuthenticationHandler {
  constructor(onSuccess) {
    super();
    this.onSuccess = onSuccess;
  }
  onRegistrationSuccess(newUser) {
    this.onSuccess(newUser);
  }
}

function CreateAccountTitle({withProgress, steps, currentStep, loginMode, email}) {
  return (
    <div>
      {withProgress && (
        <CustomProgressIndicator steps={steps} currentStep={currentStep} />
      )}
      <CreateAccountHeader>Create Account</CreateAccountHeader>
      {loginMode === "email" ? (
        <EmailConfirmationWarning email={email} />
      ) : (
        <PhoneConfirmationWarning />
      )}
    </div>
  );
}

const CustomProgressIndicator = styled(WizardProgressIndicator)`
  width: 300px;
`;

const CreateAccountHeader = styled(FormHeader)`
  margin: 30px 0;
`;

const Steps = {
  PERSONAL_INFO: "personal_info",
  CONFIRMATION: "confirmation",
  EMAIL_CONFIRMATION: "email_confirmation",
  IDENTITY_VERIFICATION: "identity_verification",
};

export default ExpressOnBoarding;
